import classNames from "classnames";
import type { HTMLComponent } from "@commercekit/react";
import { useCarousel } from "~/hooks/useCarousel";
import { useMemo, useRef, useState, useEffect } from "react";
import type { ImageField } from "@prismicio/types";

const WvPhotoCarouselSwiper: HTMLComponent<{
  images: ImageField[];
  onPreview(index: number): void;
}> = ({ images, onPreview }) => {
  const {
    emblaRef: photoRef,
    scrollTo: scrollPhotoTo,
    scrollPrev,
    scrollNext,
    selectedIndex: photoIndex,
  } = useCarousel({ loop: true, direction: "rtl" }, []);

  const scrollTo = (index: number) => {
    scrollPhotoTo(index);
  };

  const [thumbnailsCount, setThumbnailCount] = useState(5);

  const imageOptions = useMemo(
    () => images?.slice(0, thumbnailsCount),
    [images, thumbnailsCount]
  );

  const optionOverflow = useMemo(
    () => images?.length - thumbnailsCount,
    [images, thumbnailsCount]
  );

  const wrapperClasses = (index: number) => {
    return classNames(
      "product-viewport__img-wrapper product-viewport__img-wrapper--options",
      {
        "product-viewport__img-wrapper--active": photoIndex === index,
        "product-viewport__img-wrapper--inactive":
          index > thumbnailsCount - 2 && images?.length > thumbnailsCount,
      }
    );
  };

  const optionOverflowLabel = (index: number) => {
    return index > thumbnailsCount - 2 && images?.length > thumbnailsCount ? (
      <span
        onClick={() => {
          onPreview(imageOptions?.length - 1);
        }}
        className="product-viewport__count"
      >
        +{optionOverflow}
      </span>
    ) : null;
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const imgWrapperRef = useRef<HTMLDivElement>(null);

  const evaluateVisibleThumbnailsCount = () => {
    const container_width = containerRef?.current?.offsetWidth;
    const img_width = imgWrapperRef?.current?.offsetWidth || 74;
    // +15px gap between the images
    setThumbnailCount((prevCount) => {
      return container_width
        ? Math.floor(container_width / (img_width + 15))
        : prevCount;
    });
  };

  useEffect(() => {
    evaluateVisibleThumbnailsCount();

    window.addEventListener("resize", evaluateVisibleThumbnailsCount);
    return () => {
      window.removeEventListener("resize", evaluateVisibleThumbnailsCount);
    };
  }, []);

  return (
    <>
      <div className="product-swiper-wrapper">
        {images?.length && images?.length > 1 ? (
          <button
            data-icon-after=""
            className="product-viewport__nav-icon product-viewport__nav-icon--left"
            onClick={() => {
              scrollNext();
            }}
          ></button>
        ) : null}

        {images?.length && images?.length > 1 ? (
          <button
            data-icon-before=""
            className="product-viewport__nav-icon product-viewport__nav-icon--right"
            onClick={() => {
              scrollPrev();
            }}
          ></button>
        ) : null}

        <div
          className="product-viewport"
          ref={photoRef}
          onClick={() => {
            onPreview(photoIndex);
          }}
        >
          <div className="product-viewport__container">
            {images?.map((image, index) => (
              <div
                className="product-viewport__img-wrapper"
                key={`carousel_${index}`}
              >
                <img
                  src={image?.url || ""}
                  alt={image?.alt || ""}
                  className="product-viewport__img"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {images?.length && images?.length > 1 ? (
        <div className="product-viewport product-viewport--options">
          <div className="product-viewport__container" ref={containerRef}>
            {imageOptions?.map((image, index) => (
              <div
                ref={imgWrapperRef}
                className={wrapperClasses(index)}
                onClick={() => {
                  scrollTo(index);
                }}
                key={`imageOptions_${index}`}
              >
                <img
                  src={image?.url || ""}
                  alt={image?.alt || ""}
                  className="product-viewport__img"
                />
                {optionOverflowLabel(index)}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default WvPhotoCarouselSwiper;
